// /Users/joshua.moore1153/Desktop/Sinclair Apps/spcore-v4/src/components/CcpSchoolAppsAndTesting/inc/PlacementTestingForm.js
import React, {
  useState,
  useEffect,
  Fragment,
  useMemo,
  useCallback,
} from "react"; // Added useCallback
import {
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  CircularProgress,
  Grid,
  InputLabel, // Added InputLabel
  FormControl, // Added FormControl
  Select, // Added Select
  FormHelperText, // Added FormHelperText
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
// --- Import Hooks ---
import useFirestore from "../../../../config/fireBaseHook"; // Import Firestore hook
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook"; // Import Notification hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

// Helper function to format date as YYYY-MM-DD
const formatDateForSubmit = (date) => {
  if (!date) return "";
  return date.format("MM-DD-YYYY");
};

// Helper function to format numerical hour (0-23) into AM/PM string
const formatHour = (hour) => {
  if (hour === null || hour === "" || hour < 0 || hour > 23) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:00 ${ampm}`;
};

// Define available start times (8 AM to 4 PM)
const startTimes = Array.from({ length: 9 }, (_, i) => 8 + i); // Generates [8, 9, ..., 16]

const PlacementTestingForm = ({ schoolId, pathwayEmail, opEmail }) => {
  const {
    addData: addPlacementRequest,
    loading: firestoreLoading,
    error: firestoreError,
  } = useFirestore("PlacementRequests");

  const {
    sendNotification,
    loading: notificationLoading,
    error: notificationError,
  } = useSendSchoolNotification();

  const initialFormData = useMemo(
    () => ({
      // Use useMemo for initial state if props don't change often
      schoolId: schoolId,
      schoolName: "",
      fromName: "",
      testingDate: null,
      startTime: "", // New field
      endTime: "", // New field
      phone: "",
      email: "",
      testingSchedule: "",
      testingOptions: "",
      computerUsage: "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    }),
    [schoolId, pathwayEmail, opEmail]
  ); // Dependencies for initial state

  const [formData, setFormData] = useState(initialFormData);
  const [endTimeOptions, setEndTimeOptions] = useState([]); // State for dynamic end time options
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const minDate = useMemo(() => dayjs().add(14, "day"), []);

  // Update form data if props change (e.g., schoolId, emails)
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      schoolId: schoolId,
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    }));
  }, [schoolId, pathwayEmail, opEmail]);

  // Generate end time options based on selected start time
  const updateEndTimeOptions = useCallback((startHour) => {
    if (startHour === null || startHour === "") {
      setEndTimeOptions([]);
      return;
    }
    const options = [];
    for (let i = 1; i <= 4; i++) {
      const endHour = startHour + i;
      // Optional constraint: Ensure end time isn't excessively late (e.g., past 8 PM / 20:00)
      if (endHour <= 20) {
        options.push({ value: endHour, label: formatHour(endHour) });
      }
    }
    setEndTimeOptions(options);
  }, []); // No dependencies needed as formatHour is stable

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const newData = { ...prevData, [name]: value };

      // If start time changed, update end time options and reset end time
      if (name === "startTime") {
        updateEndTimeOptions(value); // value is the numerical hour
        newData.endTime = ""; // Reset end time selection
      }
      return newData;
    });
  };

  const handleDateChange = (newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      testingDate: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSnackbarMessage("");

    // Format data *before* sending
    const formattedDate = formatDateForSubmit(formData.testingDate);
    const formattedStartTime = formatHour(formData.startTime);
    const formattedEndTime = formatHour(formData.endTime);

    // --- Create the ordered email body string ---
    const emailBodyContent = `
    School Name: ${formData.schoolName}
    Contact Person Name: ${formData.fromName}
    Contact Phone: ${formData.phone}
    Contact Email: ${formData.email}
    Preferred Date of Testing: ${formattedDate}
    Start Time: ${formattedStartTime}
    End Time: ${formattedEndTime}
    Testing Schedule Support Needed: ${formData.testingSchedule}
    Testing Option(s): ${formData.testingOptions}
    Computer/Device Usage: ${formData.computerUsage}
  `.trim(); // .trim() removes leading/trailing whitespace if needed

    // Prepare data for Firestore (if it needs specific formatting)
    const dataToSendToFirestore = {
      ...formData,
      testingDate: formattedDate,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      pathwayEmail: formData.pathwayEmail || "",
      opEmail: formData.opEmail || "",
      // Keep original pathwayEmail, opEmail, schoolId etc.
    };

    // Prepare data for Notification Hook
    // Include the pre-formatted body and other necessary fields
    const dataForNotification = {
      ...formData, // Send original data too, in case backend needs it elsewhere
      testingDate: formattedDate, // Send formatted date
      startTime: formattedStartTime, // Send formatted start time
      endTime: formattedEndTime, // Send formatted end time
      emailBodyContent: emailBodyContent, // <-- Add the pre-formatted string
      // Ensure pathwayEmail and opEmail are correctly included if needed by the hook/API
      pathwayEmail: formData.pathwayEmail || "",
      opEmail: formData.opEmail || "",
    };

    const subject = "Placement Testing Request";
    const apiFileName = "PlacementTestingNotification";

    try {
      console.log(
        "Adding placement request to Firestore:",
        dataToSendToFirestore
      );
      // Send specifically formatted data to Firestore if needed
      const docRef = await addPlacementRequest(dataToSendToFirestore);
      console.log("Firestore document added with ID:", docRef.id);

      console.log("Sending school notification...");
      // Send the data structure containing the pre-formatted body
      await sendNotification(dataForNotification, subject, apiFileName);
      console.log("Notification sent successfully.");

      setSnackbarMessage(
        "Request submitted and notification sent successfully."
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error during submission:", error);
      let errorMessage = "An error occurred during submission.";
      if (firestoreError) {
        errorMessage = `Firestore error: ${
          firestoreError.message || firestoreError
        }`;
      } else if (notificationError) {
        errorMessage = `Notification error: ${
          notificationError.message || notificationError
        }`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsSubmitting(false);
    setFormData(initialFormData); // Reset form to initial state
    setEndTimeOptions([]); // Clear end time options
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Display hook errors in snackbar
  useEffect(() => {
    if (firestoreError) {
      setSnackbarMessage(
        `Firestore Error: ${firestoreError.message || firestoreError}`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [firestoreError]);

  useEffect(() => {
    if (notificationError) {
      setSnackbarMessage(
        `Notification Error: ${notificationError.message || notificationError}`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [notificationError]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fragment>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleClickOpen}
          sx={{ color: "white" }}
          disabled={isSubmitting}
        >
          New Placement Testing Request
        </Button>

        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          {" "}
          {/* Changed maxWidth to md */}
          <ModalDialogTitle onClose={handleClose}>
            Placement Testing Form
          </ModalDialogTitle>
          <DialogContent>
            {isSubmitting ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                  {" "}
                  {/* Added padding top */}
                  {/* Row 1 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="School Name"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      fullWidth
                      margin="dense" // Use dense margin
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contact Person Name"
                      name="fromName"
                      value={formData.fromName}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                    />
                  </Grid>
                  {/* Row 2 */}
                  <Grid item xs={12} sm={4}>
                    {" "}
                    {/* Adjusted grid size */}
                    <DatePicker
                      label="Preferred Date of Testing"
                      value={formData.testingDate}
                      onChange={handleDateChange}
                      minDate={minDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: "dense", // Use dense margin
                          required: true,
                          helperText: `At least 14 days from today.`,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {" "}
                    {/* Adjusted grid size */}
                    {/* Start Time Dropdown */}
                    <FormControl fullWidth required margin="dense">
                      <InputLabel id="start-time-label">Start Time</InputLabel>
                      <Select
                        labelId="start-time-label"
                        id="startTime"
                        name="startTime"
                        value={formData.startTime}
                        label="Start Time"
                        onChange={handleChange}
                      >
                        <MenuItem value="" disabled>
                          <em>Select Start Time</em>
                        </MenuItem>
                        {startTimes.map((hour) => (
                          <MenuItem key={hour} value={hour}>
                            {formatHour(hour)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {" "}
                    {/* Adjusted grid size */}
                    {/* End Time Dropdown */}
                    <FormControl
                      fullWidth
                      required
                      margin="dense"
                      disabled={
                        !formData.startTime || endTimeOptions.length === 0
                      }
                    >
                      <InputLabel id="end-time-label">End Time</InputLabel>
                      <Select
                        labelId="end-time-label"
                        id="endTime"
                        name="endTime"
                        value={formData.endTime}
                        label="End Time"
                        onChange={handleChange}
                      >
                        <MenuItem value="" disabled>
                          <em>Select End Time</em>
                        </MenuItem>
                        {endTimeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {!formData.startTime && (
                        <FormHelperText>Select start time first</FormHelperText>
                      )}
                      {formData.startTime && endTimeOptions.length === 0 && (
                        <FormHelperText>No available end times</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Row 3 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contact Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                      type="tel" // Use tel type for phone
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contact Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                    />
                  </Grid>
                  {/* Row 4 */}
                  <Grid item xs={12}>
                    <TextField
                      label="Testing Schedule Support Needed"
                      name="testingSchedule"
                      select
                      value={formData.testingSchedule}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                    >
                      <MenuItem value="" disabled>
                        <em>Select Support Needed</em>
                      </MenuItem>
                      <MenuItem value="I need Technical Phone Support to test using a Certified High School Proctor">
                        Need Tech Support (using HS Proctor)
                      </MenuItem>
                      <MenuItem value="I need to schedule testing with a Sinclair proctor (25 student min.)">
                        Need Sinclair Proctor (25 student min)
                      </MenuItem>
                    </TextField>
                  </Grid>
                  {/* Row 5 */}
                  <Grid item xs={12}>
                    <TextField
                      label="Testing Option(s)"
                      name="testingOptions"
                      select
                      value={formData.testingOptions}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                    >
                      <MenuItem value="" disabled>
                        <em>Select Testing Options</em>
                      </MenuItem>
                      <MenuItem value="Writeplacer only (approximate test time 1 hour)">
                        Writeplacer only (~1 hour)
                      </MenuItem>
                      <MenuItem value="ALEKS(math)(approximate test time 2.5 hours)">
                        ALEKS Math only (~2.5 hours)
                      </MenuItem>
                      <MenuItem value="BOTH (approximate test time 3-3.5 hours)">
                        BOTH Writeplacer & ALEKS (~3-3.5 hours)
                      </MenuItem>
                    </TextField>
                  </Grid>
                  {/* Row 6 */}
                  <Grid item xs={12}>
                    <TextField
                      label="Computer/Device Usage"
                      name="computerUsage"
                      select
                      value={formData.computerUsage}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      required
                    >
                      <MenuItem value="" disabled>
                        <em>Select Device Usage</em>
                      </MenuItem>
                      <MenuItem value="Students will be using chromebooks">
                        Students using Chromebooks
                      </MenuItem>
                      <MenuItem value="Students will be using their own pc">
                        Students using their own PCs/Laptops
                      </MenuItem>
                      <MenuItem value="Students will be in the computer lab using lab PC’s">
                        Students using Computer Lab PCs
                      </MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <ModalActions
            onSave={handleSubmit}
            saving={isSubmitting}
            saveLabel="Submit Request"
          />
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Fragment>
    </LocalizationProvider>
  );
};

export default PlacementTestingForm;
